import DOMPurify from "dompurify";
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaHeart,
  FaCheckCircle,
  FaMinus,
  FaPlus,
  FaShoppingCart,
  FaExternalLinkAlt,
  FaRegHeart,
  FaStar,
  FaRegStar,
  FaShieldAlt,
  FaUndoAlt,
  FaHeadset,
  FaChevronDown,
  FaEnvelope,
  FaWhatsapp,
} from "react-icons/fa";
import {
  MdOutlineLocalShipping,
  MdZoomIn,
  MdClose,
  MdChevronLeft,
  MdChevronRight,
  // MdBolt,
  MdLocalShipping,
  MdContentCopy,
} from "react-icons/md";
import api from "../api/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { getImageUrl } from "../utils/imageUrl";
import { ENABLE_CART_UI } from "../config/featureFlags";

// --- Sub-Component for Image Gallery (Unchanged) ---
// ... (ImageGallery component code remains the same)
const ImageGallery = ({ images, productName }) => {
  const { t } = useTranslation();
  const [mainImage, setMainImage] = useState(images[0]?.image_url);
  const [modalOpen, setModalOpen] = useState(false);

  const currentImageIndex = images.findIndex(
    (img) => img?.image_url === mainImage,
  );

  const goToPreviousImage = useCallback(() => {
    if (!images?.length) return;
    const safeIndex = currentImageIndex >= 0 ? currentImageIndex : 0;
    const previousIndex = (safeIndex - 1 + images.length) % images.length;
    setMainImage(images[previousIndex]?.image_url);
  }, [currentImageIndex, images]);

  const goToNextImage = useCallback(() => {
    if (!images?.length) return;
    const safeIndex = currentImageIndex >= 0 ? currentImageIndex : 0;
    const nextIndex = (safeIndex + 1) % images.length;
    setMainImage(images[nextIndex]?.image_url);
  }, [currentImageIndex, images]);

  useEffect(() => {
    setMainImage(images[0]?.image_url);
  }, [images]);

  // Close modal on Escape and navigate with arrow keys while open.
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") setModalOpen(false);
      if (e.key === "ArrowLeft") goToPreviousImage();
      if (e.key === "ArrowRight") goToNextImage();
    };
    if (modalOpen) window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goToNextImage, goToPreviousImage, modalOpen]);

  if (!images || images.length === 0) {
    return (
      <div className="w-full h-96 bg-gray-200 flex items-center justify-center rounded-lg">
        {t("productDetail.noImages", "No Images")}
      </div>
    );
  }

  // Ensure images are sorted by sort_order if present
  const sortedImages = Array.isArray(images)
    ? [...images].sort((a, b) => {
        if (
          typeof a.sort_order === "number" &&
          typeof b.sort_order === "number"
        ) {
          return a.sort_order - b.sort_order;
        }
        return 0;
      })
    : images;

  return (
    <div className="w-full">
      {/* Lightbox Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="relative max-w-5xl max-h-[90vh] p-2"
            onClick={(e) => e.stopPropagation()}
          >
            {images.length > 1 && (
              <>
                <button
                  className="absolute left-2 sm:-left-14 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/80 text-white rounded-full p-2 transition-colors"
                  onClick={goToPreviousImage}
                  title={t("productDetail.previousImage", "Previous image")}
                >
                  <MdChevronLeft size={28} />
                </button>
                <button
                  className="absolute right-2 sm:-right-14 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/80 text-white rounded-full p-2 transition-colors"
                  onClick={goToNextImage}
                  title={t("productDetail.nextImage", "Next image")}
                >
                  <MdChevronRight size={28} />
                </button>
              </>
            )}
            <button
              className="absolute top-3 right-3 z-10 bg-black/50 hover:bg-black/80 text-white rounded-full p-2 transition-colors"
              onClick={() => setModalOpen(false)}
            >
              <MdClose size={24} />
            </button>
            <img
              src={getImageUrl(mainImage)}
              alt={productName}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            {images.length > 1 && (
              <p className="mt-3 text-center text-xs text-white/80">
                {`${(currentImageIndex >= 0 ? currentImageIndex : 0) + 1} / ${images.length}`}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mb-4 h-96 rounded-xl overflow-hidden relative group flex items-center justify-center">
        {/* Navigation arrows (always visible if >1 image) */}
        {sortedImages.length > 1 && (
          <button
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white text-gray-700 rounded-full p-2 shadow-md transition-colors"
            onClick={goToPreviousImage}
            title={t("productDetail.previousImage", "Previous image")}
            style={{ opacity: 1 }}
          >
            <MdChevronLeft size={28} />
          </button>
        )}
        <img
          src={getImageUrl(mainImage)}
          alt={productName}
          className="w-full h-full bg-white object-contain"
        />
        {/* Zoom button */}
        <button
          className="absolute top-3 right-3 bg-white/80 hover:bg-white text-gray-700 rounded-full p-2 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          onClick={() => setModalOpen(true)}
          title={t("productDetail.zoomImage", "Zoom image")}
        >
          <MdZoomIn size={22} />
        </button>
        {sortedImages.length > 1 && (
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white text-gray-700 rounded-full p-2 shadow-md transition-colors"
            onClick={goToNextImage}
            title={t("productDetail.nextImage", "Next image")}
            style={{ opacity: 1 }}
          >
            <MdChevronRight size={28} />
          </button>
        )}
      </div>
      <div
        className="flex gap-3 overflow-x-auto p-1 scrollbar-thin scrollbar-thumb-[#e0bbe4] scrollbar-track-[#f7eaf7]"
        style={{
          WebkitOverflowScrolling: "touch",
          scrollSnapType: "x mandatory",
        }}
      >
        {sortedImages.map((img, index) => (
          <img
            key={index}
            src={getImageUrl(img.image_url)}
            alt={`Thumbnail ${index + 1}`}
            className={`w-16 h-16 min-w-[4rem] object-cover rounded-md border-2 cursor-pointer transition-all duration-200 select-none snap-center
                ${
                  img.image_url === mainImage
                    ? "border-[#AA7F96] shadow-lg scale-105"
                    : "border-gray-300 hover:border-gray-500"
                }`}
            style={{ flex: "0 0 auto" }}
            onClick={() => setMainImage(img.image_url)}
            draggable={false}
          />
        ))}
      </div>
    </div>
  );
};
// ----------------------------------------

// --- Sub-Component for Rendering Variations (Compact with hover gradient overlay & live preview) ---
const GroupedVariations = ({
  variations,
  currentProduct,
  onSelect,
  onHoverVariant,
  onHoverEnd,
  t,
}) => {
  const [hoveredVariant, setHoveredVariant] = useState(null);

  if (variations.length <= 1) return null;

  // Group variations by variation_type (e.g., Color, Size)
  const grouped = variations.reduce((acc, v) => {
    const type = v.variation_type || "Options";
    if (!acc[type]) acc[type] = [];
    acc[type].push(v);
    return acc;
  }, {});

  return (
    <div className="space-y-6 mt-4 pt-4 border-t border-gray-200">
      {Object.entries(grouped).map(([type, items]) => {
        // Check if ANY item has an image (not just the first one)
        const renderAsImage = items.some((v) => v.images?.[0]?.image_url);

        // Sort items for consistent ordering
        const sortedItems = [...items].sort((a, b) => {
          const aVal = String(a.variation_value || "").toLowerCase();
          const bVal = String(b.variation_value || "").toLowerCase();
          return aVal.localeCompare(bVal);
        });

        return (
          <div key={type}>
            {/* Section Title */}
            <h4 className="text-lg font-bold text-gray-800 mb-3">
              {t("selectVariation", `${type}`)}:{" "}
              <span className="font-normal text-[#AA7F96]">
                {hoveredVariant?.variation_value ||
                  currentProduct.variation_value ||
                  ""}
              </span>
            </h4>

            {/* Variant Flex Grid */}
            <div className="flex flex-wrap gap-[0.5rem] lg:gap-3">
              {sortedItems.map((v) => {
                const isActive = v.product_id === currentProduct.product_id;
                const value = v.variation_value || v.product_id;
                const outOfStock = v.stock_quantity === 0;
                const imageUrl = v.images?.[0]?.image_url;
                const variantPrice = v.sale_price || v.price;

                if (renderAsImage && imageUrl) {
                  // --- Compact Thumbnail with Hover Gradient Overlay ---
                  return (
                    <button
                      key={v.product_id}
                      onClick={() => !outOfStock && onSelect(v.product_id)}
                      onMouseEnter={() => {
                        setHoveredVariant(v);
                        if (!outOfStock) onHoverVariant?.(v);
                      }}
                      onMouseLeave={() => {
                        setHoveredVariant(null);
                        onHoverEnd?.();
                      }}
                      title={`${value}${outOfStock ? " - " + t("productDetail.stock.outOfStock", "Out of Stock") : ""}`}
                      className={`group relative w-24 h-24 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                        isActive
                          ? "ring-2 ring-[#AA7F96] ring-offset-2 shadow-md"
                          : outOfStock
                            ? "border-gray-200"
                            : "border-gray-300 hover:border-gray-400"
                      } ${outOfStock ? "cursor-not-allowed" : ""}`}
                    >
                      {/* Product Image — always shown, dimmed if out of stock */}
                      <img
                        src={getImageUrl(imageUrl)}
                        alt={value}
                        className={`w-full h-full object-cover transition-transform duration-200 ${
                          outOfStock ? "opacity-40" : "group-hover:scale-105"
                        }`}
                      />

                      {/* Amazon-style diagonal strikethrough for out of stock */}
                      {outOfStock && (
                        <div className="absolute inset-0 overflow-hidden pointer-events-none">
                          <div
                            className="absolute bg-gray-400/70"
                            style={{
                              width: "140%",
                              height: "2px",
                              top: "50%",
                              left: "-20%",
                              transform: "rotate(-45deg)",
                              transformOrigin: "center",
                            }}
                          />
                        </div>
                      )}

                      {/* Hover Overlay with Variant Info - Gradient Dark Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-center justify-end p-2">
                        <p className="text-white text-xs font-semibold text-center truncate w-full">
                          {value}
                        </p>
                        {outOfStock ? (
                          <p className="text-red-300 text-xs mt-0.5">
                            {t(
                              "productDetail.stock.outOfStock",
                              "Out of Stock",
                            )}
                          </p>
                        ) : (
                          <p className="text-yellow-300 text-xs font-bold mt-0.5">
                            {variantPrice
                              ? `${Number(variantPrice).toLocaleString()} TL`
                              : ""}
                          </p>
                        )}
                      </div>

                      {/* Active Checkmark Badge */}
                      {isActive && (
                        <div className="absolute top-1 right-1 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-md">
                          ✓
                        </div>
                      )}
                    </button>
                  );
                }

                if (renderAsImage && !imageUrl) {
                  // --- No image for this variant but group uses images: show placeholder thumbnail ---
                  return (
                    <button
                      key={v.product_id}
                      onClick={() => !outOfStock && onSelect(v.product_id)}
                      onMouseEnter={() => setHoveredVariant(v)}
                      onMouseLeave={() => setHoveredVariant(null)}
                      title={`${value}${outOfStock ? " - " + t("productDetail.stock.outOfStock", "Out of Stock") : ""}`}
                      className={`group relative w-24 h-24 rounded-lg overflow-hidden border-2 bg-gray-100 transition-all duration-200 flex items-center justify-center ${
                        isActive
                          ? "ring-2 ring-[#AA7F96] ring-offset-2 shadow-md"
                          : outOfStock
                            ? "border-gray-200 cursor-not-allowed"
                            : "border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      <span
                        className={`text-xs font-medium text-center px-1 line-clamp-3 ${outOfStock ? "line-through text-gray-400" : "text-gray-600"}`}
                      >
                        {value}
                      </span>
                      {outOfStock && (
                        <div className="absolute inset-0 overflow-hidden pointer-events-none">
                          <div
                            className="absolute bg-gray-400/70"
                            style={{
                              width: "140%",
                              height: "2px",
                              top: "50%",
                              left: "-20%",
                              transform: "rotate(-45deg)",
                              transformOrigin: "center",
                            }}
                          />
                        </div>
                      )}
                    </button>
                  );
                }

                // --- Fallback Pill Button (for size or when no image in the whole group) ---
                return (
                  <button
                    key={v.product_id}
                    onClick={() => !outOfStock && onSelect(v.product_id)}
                    className={`relative px-4 py-2 text-sm border rounded-lg transition-all duration-200 ${
                      isActive
                        ? "bg-[#AA7F96] text-white border-[#AA7F96] shadow-md font-semibold"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                    } ${outOfStock ? "opacity-50 cursor-not-allowed" : ""}`}
                    title={value}
                  >
                    <span className={outOfStock ? "line-through" : ""}>
                      {value}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
// ----------------------------------------

const PAGE_KEY_TO_MAIN_ROUTE = {
  wheatbag: "/wheatbag",
  gift: "/giftProducts",
  kitchen: "/kitchenProducts",
  decor: "/wallarts",
  handmade: "/handmadeProducts",
  natural: "/naturalProducts",
  camping: "/campingOutdoors",
  fridge: "/fridgeDeo",
  japanese: "/japaneseProducts",
};

const PAGE_KEY_TO_TITLE_TRANSLATION = {
  wheatbag: "navbar.productsMenu.wheatBag",
  gift: "products.routes.gifts.title",
  kitchen: "products.routes.kitchen.title",
  decor: "products.routes.decor.title",
  handmade: "products.routes.handmade.title",
  natural: "products.routes.natural.title",
  camping: "products.routes.camping.title",
  fridge: "products.routes.fridge.title",
  japanese: "products.routes.japanese.title",
};

const resolveProductMainPage = (product) => {
  const placements = Array.isArray(product?.pagePlacements)
    ? [...product.pagePlacements]
    : [];

  const mainPlacementKey = placements
    .sort(
      (a, b) => Number(a?.sort_order ?? 9999) - Number(b?.sort_order ?? 9999),
    )
    .map((placement) => String(placement?.page_key || "").toLowerCase())
    .find((key) => key && key !== "home");

  if (mainPlacementKey && PAGE_KEY_TO_MAIN_ROUTE[mainPlacementKey]) {
    return {
      route: PAGE_KEY_TO_MAIN_ROUTE[mainPlacementKey],
      pageKey: mainPlacementKey,
    };
  }

  const normalizedTags = Array.isArray(product?.tags)
    ? product.tags.map((tag) => String(tag).toLowerCase())
    : [];

  if (normalizedTags.some((tag) => tag.includes("wheat"))) {
    return { route: PAGE_KEY_TO_MAIN_ROUTE.wheatbag, pageKey: "wheatbag" };
  }
  if (normalizedTags.some((tag) => tag.includes("japan"))) {
    return { route: PAGE_KEY_TO_MAIN_ROUTE.japanese, pageKey: "japanese" };
  }
  if (normalizedTags.some((tag) => tag.includes("fridge"))) {
    return { route: PAGE_KEY_TO_MAIN_ROUTE.fridge, pageKey: "fridge" };
  }

  return { route: "/products", pageKey: null };
};

const getProductRouteParam = (product) =>
  product?.product_slug || product?.product_id;

const matchesProductRouteParam = (candidate, routeParam) =>
  String(candidate?.product_id) === String(routeParam) ||
  String(candidate?.product_slug || "") === String(routeParam);

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const {
    cartItems = [],
    addItemToCart,
    updateItemQuantity,
    toggleSidebar,
  } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isWishlistLoading, setIsWishlistLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewAccessLoading, setReviewAccessLoading] = useState(false);
  const [reviewSort, setReviewSort] = useState("newest");
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [selectedShipping, setSelectedShipping] = useState("standard");
  const [newReviewImage, setNewReviewImage] = useState(null);
  const [newReviewImagePreview, setNewReviewImagePreview] = useState("");
  const [removeExistingReviewImage, setRemoveExistingReviewImage] =
    useState(false);
  const [currentUserReview, setCurrentUserReview] = useState(null);
  const [canReviewProduct, setCanReviewProduct] = useState(false);
  const [openQaId, setOpenQaId] = useState("shipping");
  const [newReview, setNewReview] = useState({ rating: 5, comment: "" });
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);
  const [inquiryType, setInquiryType] = useState("quote");
  const [inquiryFeedback, setInquiryFeedback] = useState("");
  const [inquiryForm, setInquiryForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    quantity: 1,
    message: "",
  });
  const [quantity, setQuantity] = useState(1);
  const [localCartItem, setLocalCartItem] = useState(null);
  const [variations, setVariations] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedProductsLoading, setRelatedProductsLoading] = useState(false);
  const [hoveredProduct, setHoveredProduct] = useState(null);

  const currentLanguage = i18n.language;
  const resolvedUserId = user?.id || user?.user_id || user?.userId;
  const resolvedReviewProductId =
    product?.product_id ||
    (Number.isInteger(Number(productId)) ? Number(productId) : null);

  const getLocalizedName = useCallback(
    (p) => p[`name_${currentLanguage}`] || p.name_en,
    [currentLanguage],
  );

  const getLocalizedDescription = useCallback(
    (p) => p[`description_${currentLanguage}`] || p.description_en,
    [currentLanguage],
  );

  useEffect(() => {
    let isActive = true;

    if (!product || !user) {
      setIsFavorited(false);
      return;
    }

    const syncWishlistState = async () => {
      try {
        const response = await api.get("/user/wishlist");
        const wishlistItems = Array.isArray(response.data) ? response.data : [];
        const isProductInWishlist = wishlistItems.some(
          (item) => item.product_id === product.product_id,
        );

        if (isActive) {
          setIsFavorited(isProductInWishlist);
        }
      } catch (wishlistError) {
        console.error("Failed to load wishlist:", wishlistError);
        if (isActive) {
          setIsFavorited(false);
        }
      }
    };

    syncWishlistState();

    return () => {
      isActive = false;
    };
  }, [product, user]);

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();

    if (!user || !product) {
      alert(t("loginRequired", "Please log in to use wishlist."));
      navigate("/login");
      return;
    }

    if (isWishlistLoading) return;

    const previousValue = isFavorited;
    const optimisticValue = !previousValue;
    setIsFavorited(optimisticValue);
    setIsWishlistLoading(true);

    try {
      const response = await api.post("/user/wishlist/toggle", {
        productId: product.product_id,
      });

      setIsFavorited(response.data.action === "added");
    } catch (wishlistError) {
      console.error("Failed to toggle wishlist:", wishlistError);
      setIsFavorited(previousValue);
      alert(t("wishlistError", "Could not update wishlist right now."));
    } finally {
      setIsWishlistLoading(false);
    }
  };

  const refreshReviews = useCallback(async () => {
    if (!resolvedReviewProductId) return;
    setReviewsLoading(true);
    try {
      const response = await api.get(
        `/products/${resolvedReviewProductId}/reviews`,
      );
      setReviews(Array.isArray(response.data) ? response.data : []);
    } catch (reviewsError) {
      console.error("Failed to fetch reviews:", reviewsError);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  }, [resolvedReviewProductId]);

  const loadCurrentUserReview = useCallback(async () => {
    if (!resolvedReviewProductId || !user) {
      setCurrentUserReview(null);
      setCanReviewProduct(false);
      return;
    }

    setReviewAccessLoading(true);
    try {
      const response = await api.get(
        `/products/${resolvedReviewProductId}/reviews/me`,
      );
      setCurrentUserReview(response.data?.review || null);
      setCanReviewProduct(Boolean(response.data?.canReview));
    } catch (reviewAccessError) {
      console.error("Failed to fetch current user review:", reviewAccessError);
      setCurrentUserReview(null);
      setCanReviewProduct(false);
    } finally {
      setReviewAccessLoading(false);
    }
  }, [resolvedReviewProductId, user]);

  useEffect(() => {
    refreshReviews();
  }, [refreshReviews]);

  useEffect(() => {
    loadCurrentUserReview();
  }, [loadCurrentUserReview]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (!user || !resolvedUserId) {
      alert(t("loginRequired", "Please log in to leave a review."));
      navigate("/login");
      return;
    }

    if (!currentUserReview && !canReviewProduct) {
      alert(
        t(
          "reviewPurchaseRequired",
          "Only customers who purchased this product can review it.",
        ),
      );
      return;
    }

    if (!newReview.comment.trim()) {
      alert(t("reviewCommentRequired", "Please write a short review comment."));
      return;
    }

    setIsSubmittingReview(true);
    try {
      const endpoint = currentUserReview
        ? `/products/${resolvedReviewProductId}/reviews/me`
        : `/products/${resolvedReviewProductId}/reviews`;

      if (newReviewImage || removeExistingReviewImage) {
        const formData = new FormData();
        formData.append("rating", String(newReview.rating));
        formData.append("comment", newReview.comment.trim());
        if (newReviewImage) {
          formData.append("image", newReviewImage);
        }
        if (removeExistingReviewImage) {
          formData.append("removeImage", "true");
        }

        const backendUrl =
          process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
        const uploadResponse = await fetch(`${backendUrl}/api${endpoint}`, {
          method: currentUserReview ? "PATCH" : "POST",
          credentials: "include",
          body: formData,
        });

        if (!uploadResponse.ok) {
          let uploadError = "Could not submit review right now.";
          try {
            const errorPayload = await uploadResponse.json();
            uploadError =
              errorPayload?.error || errorPayload?.message || uploadError;
          } catch {
            // Keep fallback message when response is not JSON.
          }
          throw new Error(uploadError);
        }
      } else if (currentUserReview) {
        await api.patch(endpoint, {
          rating: newReview.rating,
          comment: newReview.comment.trim(),
          removeImage: false,
        });
      } else {
        await api.post(endpoint, {
          rating: newReview.rating,
          comment: newReview.comment.trim(),
        });
      }

      await Promise.all([refreshReviews(), loadCurrentUserReview()]);
      closeReviewModal();
    } catch (submitError) {
      console.error("Failed to submit review:", submitError);
      const apiMessage =
        submitError?.response?.data?.error ||
        submitError?.response?.data?.message ||
        submitError?.message ||
        "Could not submit review right now.";
      alert(t("reviewSubmitError", apiMessage));
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleReviewImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedMimeTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
    ];
    if (!allowedMimeTypes.includes(file.type)) {
      alert(
        t(
          "productDetail.reviews.imageTypeError",
          "Please upload a JPEG, PNG, WebP, or GIF image.",
        ),
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert(
        t(
          "productDetail.reviews.imageSizeError",
          "Image size must be 5MB or less.",
        ),
      );
      return;
    }

    if (newReviewImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(newReviewImagePreview);
    }

    setRemoveExistingReviewImage(false);
    setNewReviewImage(file);
    setNewReviewImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveReviewImage = () => {
    if (newReviewImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(newReviewImagePreview);
    }
    setNewReviewImage(null);
    setNewReviewImagePreview("");
    setRemoveExistingReviewImage(Boolean(currentUserReview?.image_url));
  };

  const openReviewModal = () => {
    if (!user || !resolvedUserId) {
      alert(t("loginRequired", "Please log in to leave a review."));
      navigate("/login");
      return;
    }

    if (!currentUserReview && !canReviewProduct) {
      alert(
        t(
          "reviewPurchaseRequired",
          "Only customers who purchased this product can review it.",
        ),
      );
      return;
    }

    setIsReviewModalOpen(true);
    setRemoveExistingReviewImage(false);
    setNewReviewImage(null);

    if (currentUserReview) {
      setNewReview({
        rating: currentUserReview.rating || 5,
        comment: currentUserReview.comment || "",
      });
      setNewReviewImagePreview(
        currentUserReview.image_url
          ? getImageUrl(currentUserReview.image_url)
          : "",
      );
      return;
    }

    if (newReviewImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(newReviewImagePreview);
    }
    setNewReview({ rating: 5, comment: "" });
    setNewReviewImagePreview("");
  };

  const closeReviewModal = () => {
    setIsReviewModalOpen(false);
    setNewReview({ rating: 5, comment: "" });
    setNewReviewImage(null);
    setRemoveExistingReviewImage(false);
    if (newReviewImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(newReviewImagePreview);
    }
    setNewReviewImagePreview("");
  };

  const sortedReviews = useMemo(() => {
    const list = [...reviews];
    if (reviewSort === "highest") {
      return list.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }
    if (reviewSort === "lowest") {
      return list.sort((a, b) => (a.rating || 0) - (b.rating || 0));
    }
    return list.sort(
      (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
    );
  }, [reviews, reviewSort]);

  const averageRating = useMemo(() => {
    if (!reviews.length) return 0;
    const total = reviews.reduce((sum, item) => sum + (item.rating || 0), 0);
    return total / reviews.length;
  }, [reviews]);

  const ratingsBreakdown = useMemo(() => {
    const base = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    reviews.forEach((item) => {
      const value = Math.max(1, Math.min(5, Number(item.rating) || 0));
      if (value) base[value] += 1;
    });
    return base;
  }, [reviews]);

  const formatReviewerName = (review) => {
    const firstName =
      review?.User?.first_name?.trim() ||
      review?.user?.first_name?.trim() ||
      (review?.user_id === (user?.id || user?.user_id || user?.userId)
        ? user?.first_name?.trim()
        : "") ||
      "";
    const lastName =
      review?.User?.last_name?.trim() ||
      review?.user?.last_name?.trim() ||
      (review?.user_id === (user?.id || user?.user_id || user?.userId)
        ? user?.last_name?.trim()
        : "") ||
      "";

    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : "C";
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : "X";

    return `${firstInitial}**** ${lastInitial}****`;
  };

  // --- Variation Fetching Function (Unchanged) ---
  const fetchVariations = useCallback(async (modelCode) => {
    if (!modelCode) return;
    try {
      // API call remains the same, assuming it returns product objects with images array
      const response = await api.get(`/products/variations/${modelCode}`);
      setVariations(response.data.products || response.data);
    } catch (err) {
      console.error("Failed to fetch product variations:", err);
      setVariations([]);
    }
  }, []);

  // --- Related Products Fetching Function ---
  const fetchRelatedProducts = useCallback(async (relatedIds) => {
    if (!Array.isArray(relatedIds) || relatedIds.length === 0) {
      setRelatedProducts([]);
      return;
    }

    setRelatedProductsLoading(true);
    try {
      const response = await api.get(`/products/related`, {
        params: { ids: relatedIds.join(",") },
      });
      setRelatedProducts(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to fetch related products:", err);
      setRelatedProducts([]);
    } finally {
      setRelatedProductsLoading(false);
    }
  }, []);

  const syncRelatedProductsForProduct = useCallback(
    (candidateProduct) => {
      if (
        candidateProduct?.related_product_ids &&
        candidateProduct.related_product_ids.length > 0
      ) {
        fetchRelatedProducts(candidateProduct.related_product_ids);
      } else {
        setRelatedProducts([]);
      }
    },
    [fetchRelatedProducts],
  );
  // ------------------------------------

  // Keep track of last loaded id to avoid re-fetch & loader flicker
  const lastLoadedId = useRef(null);

  // Ensure syncCartState helper is defined before using (keep existing helper)
  const syncCartState = useCallback(
    (fetchedProduct, cartItemsArg = cartItems) => {
      const validCartItems = (cartItemsArg || []).filter(Boolean);
      const itemInCart = validCartItems.find(
        (item) => item.product_id === fetchedProduct.product_id,
      );
      setLocalCartItem(itemInCart);
      setQuantity(itemInCart ? itemInCart.quantity : 1);
    },
    [cartItems],
  );

  // Fast-path: if navigation passed a product, render it immediately and don't show loader
  useEffect(() => {
    const preloaded = location.state?.product;
    if (preloaded && preloaded.product_id) {
      setProduct(preloaded);
      lastLoadedId.current = getProductRouteParam(preloaded);
      syncCartState(preloaded);
      if (preloaded.model_code) fetchVariations(preloaded.model_code);
      syncRelatedProductsForProduct(preloaded);
      setLoading(false); // important: clear loader so page doesn't stick on Loading
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state]);

  // --- Product Fetching and Cart Sync (less eager loading to prevent flicker) ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchProductAndSyncCart = useCallback(
    async (id = productId) => {
      // Fast path: if this id is already loaded (via variation click), skip network & loader
      if (lastLoadedId.current && String(lastLoadedId.current) === String(id)) {
        return;
      }

      // If product exists in variations, use it locally (avoid loader)
      const localFromVariations = variations.find((v) =>
        matchesProductRouteParam(v, id),
      );
      if (localFromVariations) {
        setProduct(localFromVariations);
        syncCartState(localFromVariations);
        if (localFromVariations.model_code)
          fetchVariations(localFromVariations.model_code);
        syncRelatedProductsForProduct(localFromVariations);
        lastLoadedId.current = getProductRouteParam(localFromVariations);
        return;
      }

      setLoading(true);
      try {
        const response = await api.get(`/products/${id}`);
        const fetchedProduct = response.data?.product ?? response.data;

        if (!fetchedProduct) {
          setError(t("productDetail.productNotFound", "Product not found."));
          setProduct(null);
          setLoading(false);
          return;
        }

        setProduct(fetchedProduct);

        if (fetchedProduct.model_code) {
          fetchVariations(fetchedProduct.model_code);
        } else {
          setVariations([]);
        }

        if (
          fetchedProduct.related_product_ids &&
          fetchedProduct.related_product_ids.length > 0
        ) {
          fetchRelatedProducts(fetchedProduct.related_product_ids);
        } else {
          setRelatedProducts([]);
        }

        syncCartState(fetchedProduct);
        lastLoadedId.current = getProductRouteParam(fetchedProduct);
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch product:", err);
        setError(
          t(
            "productDetail.productLoadError",
            "Product not found or an error occurred.",
          ),
        );
        setLoading(false);
      }
    },
    [
      productId,
      fetchVariations,
      fetchRelatedProducts,
      syncRelatedProductsForProduct,
      variations,
      syncCartState,
      t,
    ],
  );

  useEffect(() => {
    // don't re-fetch (and show loader) if we already have this product loaded
    if (
      lastLoadedId.current &&
      String(lastLoadedId.current) === String(productId)
    ) {
      return;
    }
    fetchProductAndSyncCart(productId);
  }, [fetchProductAndSyncCart, productId]);

  // --- Handler for clicking a variation button (UPDATED) ---
  const handleVariationClick = (newProductId) => {
    // If variation exists locally, update locally and mark loaded to avoid fetch/loader
    const newProductData = variations.find(
      (v) => String(v.product_id) === String(newProductId),
    );
    if (newProductData) {
      const routeParam = getProductRouteParam(newProductData) || newProductId;
      lastLoadedId.current = routeParam;
      setProduct(newProductData);
      syncCartState(newProductData);
      syncRelatedProductsForProduct(newProductData);
      navigate(`/product/${routeParam}`, { replace: true });
      window.scrollTo(0, 0);
      return;
    }

    // fallback: navigate and let effect fetch
    navigate(`/product/${newProductId}`);
    window.scrollTo(0, 0);
  };

  // --- Cart/Stock Logic (Omitted for brevity, assumed unchanged) ---
  const isOutOfStock = product && product.stock_quantity === 0;
  const stockQuantity = product?.stock_quantity || 0;
  const desiredTotalQuantity = quantity;

  const handleQuantityChange = (delta) => {
    const newQuantity = Math.max(1, quantity + delta);

    if (newQuantity <= stockQuantity) {
      setQuantity(newQuantity);
    } else if (newQuantity > stockQuantity) {
      alert(
        t(
          "maxStockReached",
          `Cannot add more. Only ${stockQuantity} available in stock`,
        ),
      );
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      alert(t("loginRequired", "Please log in to add items to your cart."));
      return;
    }
    if (isOutOfStock) return;

    try {
      if (localCartItem) {
        await updateItemQuantity(
          localCartItem.cart_item_id,
          desiredTotalQuantity,
        );
      } else {
        await addItemToCart(product.product_id, desiredTotalQuantity);
      }
      toggleSidebar(true);
    } catch (error) {
      console.error("Failed to update cart:", error);
      alert(t("cartError", "Could not add item to cart."));
    }
  };

  const productMainPage = useMemo(
    () => resolveProductMainPage(product),
    [product],
  );

  const productMainPageTitle = useMemo(() => {
    if (!productMainPage.pageKey) {
      return t("products.routes.all.title", "All Products");
    }

    const translationKey =
      PAGE_KEY_TO_TITLE_TRANSLATION[productMainPage.pageKey];
    if (!translationKey) {
      return t("products.routes.all.title", "All Products");
    }

    return t(translationKey);
  }, [productMainPage.pageKey, t]);

  // --- Render Logic (Omitted for brevity, assumed unchanged) ---
  if (loading) {
    return (
      <div className="p-8 text-center text-xl">
        {t("loading", "Loading product details...")}
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="p-8 text-center text-red-600 text-xl">
        {t("error", error)}
      </div>
    );
  }

  const {
    price,
    sale_price,
    model_code,
    sku,
    tags = [],
    trendyol_link,
    amazon_link,
    amazon_japan_link,
    hepsiburada_link,
    images = [],
  } = product;

  // Use hovered variant's images if hovering, otherwise use current product's images
  const displayImages = hoveredProduct?.images || images;

  const marketplaceButtons = [
    {
      key: "trendyol",
      label: t("productDetail.marketplaces.trendyol", "Buy on Trendyol"),
      href: trendyol_link,
      gradient: "linear-gradient(90deg, #F27A1A, #e06010)",
    },
    {
      key: "hepsiburada",
      label: t("productDetail.marketplaces.hepsiburada", "Buy on Hepsiburada"),
      href: hepsiburada_link,
      gradient: "linear-gradient(90deg, #FF6000, #e05500)",
    },
    {
      key: "amazon",
      label: t("productDetail.marketplaces.amazon", "Buy on Amazon"),
      href: amazon_link,
      gradient: "linear-gradient(90deg, #232F3E, #37475A)",
    },
    {
      key: "amazon_japan",
      label: t("productDetail.marketplaces.amazonJapan", "Buy on Amazon Japan"),
      href: amazon_japan_link,
      gradient: "linear-gradient(90deg, #4c6585, #16212e)",
    },
  ];

  const shippingOptions = [
    {
      key: "standard",
      title: t("productDetail.shipping.standardTitle", "Standard Delivery"),
      eta: t("productDetail.shipping.standardEta", "2-4 business days"),
      price: "150 TL",
      icon: <MdLocalShipping className="text-xl" />,
    },
    // {
    //   key: "express",
    //   title: t("productDetail.shipping.expressTitle", "Express Delivery"),
    //   eta: t("productDetail.shipping.expressEta", "Next day in major cities"),
    //   price: "250 TL",
    //   icon: <MdBolt className="text-xl" />,
    // },
    {
      key: "free",
      title: t("productDetail.shipping.freeTitle", "Free Shipping"),
      eta: t("productDetail.shipping.freeEta", "3-5 business days"),
      price: "0 TL (1000+ TL orders)",
      icon: <MdOutlineLocalShipping className="text-xl" />,
    },
  ];

  const selectedShippingInfo =
    shippingOptions.find((option) => option.key === selectedShipping) ||
    shippingOptions[0];

  // uncomment and adjust free shipping threshold and progress logic as needed
  // const freeShippingThreshold = 100;
  // const numericPrice = Number(price) || 0;
  // const remainingForFreeShipping = Math.max(
  //   0,
  //   freeShippingThreshold - numericPrice,
  // );
  // const freeShippingProgress = Math.min(
  //   2000,
  //   Math.round((numericPrice / freeShippingThreshold) * 100),
  // );

  const qaItems = [
    {
      id: "shipping",
      question: t(
        "productDetail.qa.shippingQuestion",
        "How does shipping work for this product?",
      ),
      answer: t(
        "productDetail.qa.shippingAnswer",
        "You can choose Standard, Express, or Free Shipping at checkout. Delivery speed depends on your city and selected courier.",
      ),
    },
    {
      id: "returns",
      question: t(
        "productDetail.qa.returnsQuestion",
        "Can I return the product if it does not fit my needs?",
      ),
      answer: t(
        "productDetail.qa.returnsAnswer",
        "Yes. Returns are accepted for eligible items in original condition. Contact support from your profile page and we will guide the return pickup.",
      ),
    },
    {
      id: "authenticity",
      question: t(
        "productDetail.qa.authenticityQuestion",
        "Are products original and quality-checked?",
      ),
      answer: t(
        "productDetail.qa.authenticityAnswer",
        "Yes. Products are sourced through verified suppliers and pass a quality control check before shipment.",
      ),
    },
  ];

  const salesEmail =
    process.env.REACT_APP_SALES_EMAIL || "info@crossborder-store.com";
  const whatsappNumber = String(
    process.env.REACT_APP_WHATSAPP_NUMBER || "+905525510595",
  ).replace(/\D/g, "");

  const buildDefaultInquiryMessage = () => {
    const productName = getLocalizedName(product || {});
    const pageUrl =
      typeof window !== "undefined" ? window.location.href : "Product page";

    return [
      t("productDetail.inquiry.greeting", "Hello sales team,"),
      "",
      inquiryType === "quote"
        ? t(
            "productDetail.inquiry.quoteIntro",
            "I would like to request a quote for the following product:",
          )
        : t(
            "productDetail.inquiry.productIntro",
            "I would like to ask about the following product:",
          ),
      `- ${t("productDetail.inquiry.productLabel", "Product")}: ${productName}`,
      `- ${t("productDetail.inquiry.modelLabel", "Model/SKU")}: ${model_code || sku || t("profile.na", "N/A")}`,
      `- ${t("productDetail.inquiry.quantityLabel", "Quantity")}: ${Math.max(1, Number(quantity) || 1)}`,
      `- ${t("productDetail.inquiry.unitPriceLabel", "Unit Price (listed)")}: ${price} TL`,
      `- ${t("productDetail.inquiry.urlLabel", "Product URL")}: ${pageUrl}`,
      "",
      t("productDetail.inquiry.detailsLabel", "Details:"),
    ].join("\n");
  };

  const openInquiryModal = (type = "quote") => {
    setInquiryType(type);
    setInquiryFeedback("");

    const fallbackName = [user?.first_name, user?.last_name]
      .filter(Boolean)
      .join(" ");

    setInquiryForm((prev) => ({
      ...prev,
      fullName: prev.fullName || fallbackName,
      email: prev.email || user?.email || "",
      quantity: Math.max(1, Number(quantity) || 1),
      message: prev.message || buildDefaultInquiryMessage(),
    }));
    setIsInquiryModalOpen(true);
  };

  const closeInquiryModal = () => {
    setIsInquiryModalOpen(false);
    setInquiryFeedback("");
  };

  const handleInquiryFieldChange = (field, value) => {
    setInquiryForm((prev) => ({
      ...prev,
      [field]: field === "quantity" ? Math.max(1, Number(value) || 1) : value,
    }));
  };

  const composedInquiryMessage = [
    inquiryForm.message,
    "",
    `${t("productDetail.inquiry.contactName", "Contact name")}: ${inquiryForm.fullName || t("profile.na", "N/A")}`,
    `${t("productDetail.inquiry.emailLabel", "Email")}: ${inquiryForm.email || t("profile.na", "N/A")}`,
    `${t("productDetail.inquiry.phoneLabel", "Phone")}: ${inquiryForm.phone || t("profile.na", "N/A")}`,
    `${t("productDetail.inquiry.companyLabel", "Company")}: ${inquiryForm.company || t("profile.na", "N/A")}`,
  ].join("\n");

  const inquiryEmailSubject = `${
    inquiryType === "quote"
      ? t("productDetail.inquiry.quoteRequest", "Quote Request")
      : t("productDetail.inquiry.productInquiry", "Product Inquiry")
  } - ${getLocalizedName(product)}`;

  const inquiryEmailHref = `mailto:${salesEmail}?subject=${encodeURIComponent(
    inquiryEmailSubject,
  )}&body=${encodeURIComponent(composedInquiryMessage)}`;

  const inquiryWhatsappHref = whatsappNumber
    ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
        `${inquiryEmailSubject}\n\n${composedInquiryMessage}`,
      )}`
    : "";

  const copyInquiryMessage = async () => {
    try {
      await navigator.clipboard.writeText(composedInquiryMessage);
      setInquiryFeedback(
        t(
          "productDetail.inquiry.copySuccess",
          "Message copied. You can paste it anywhere.",
        ),
      );
    } catch (copyError) {
      console.error("Failed to copy inquiry message:", copyError);
      setInquiryFeedback(
        t(
          "productDetail.inquiry.copyError",
          "Could not copy message. Please copy manually.",
        ),
      );
    }
  };

  return (
    // keep a stable min-height so layout doesn't collapse while fetching (prevents footer jump)
    <div className=" mx-auto p-6 lg:px-[11rem] lg:py-[6rem] min-h-[70vh]">
      <div className="flex flex-col lg:flex-row gap-8">
        {/* 1. Image Gallery (Left Section) */}
        <div className="lg:w-2/5 p-4">
          <ImageGallery
            images={displayImages}
            productName={getLocalizedName(product)}
          />
        </div>

        {/* 2. Product Information (Middle Section) */}
        <div className="lg:w-2/5 p-4 border-l border-gray-100">
          <h1 className="text-3xl font-normal text-gray-900 mb-4">
            {getLocalizedName(product)}
          </h1>
          <button
            onClick={() => openInquiryModal("quote")}
            className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-[#83375B] hover:text-[#9b4b70]"
          >
            <FaEnvelope size={13} />
            {t(
              "productDetail.bulkQuoteCta",
              "Need bulk pricing? Request a quote",
            )}
          </button>

          <div className="mb-4 rounded-xl border border-[#ecd6df] bg-[linear-gradient(145deg,#fff7fa_0%,#fffdfb_60%,#fdf0f5_100%)] p-3 shadow-[0_14px_34px_-28px_rgba(131,55,91,0.6)]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9b5b77]">
              {t(
                "productDetail.mainPage.label",
                "Explore this product in its main page",
              )}
            </p>
            <Link
              to={productMainPage.route}
              className="mt-2 inline-flex w-full items-center justify-between gap-3 rounded-lg bg-white px-3 py-2.5 text-left text-sm font-semibold text-[#7e3957] transition-all duration-200 hover:bg-[#fff4f8] hover:shadow-sm"
            >
              <span className="line-clamp-1">{productMainPageTitle}</span>
              <FaExternalLinkAlt className="shrink-0 text-xs text-[#9f5d79]" />
            </Link>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            {t("modelCode", "Model")}:{" "}
            <span className="font-medium text-gray-700">
              {model_code || sku}
            </span>
          </p>

          {sale_price ? (
            <div className="mb-4">
              <p className="text-lg text-gray-500 line-through">{price} TL</p>
              <h2 className="text-4xl font-bold text-red-600">
                {sale_price} TL
              </h2>
            </div>
          ) : (
            <h2 className="text-4xl font-bold text-[#83375B] mb-4">
              {price} TL
            </h2>
          )}

          {/* 🌟 NEW: Product Variations Block (Renders images if available) */}
          <GroupedVariations
            variations={variations}
            currentProduct={product}
            onSelect={handleVariationClick}
            onHoverVariant={(v) => setHoveredProduct(v)}
            onHoverEnd={() => setHoveredProduct(null)}
            t={t}
          />
          {/* ----------------------------------------------------------------- */}

          {/* Stock Status */}
          <div className="my-6 p-4 bg-gray-50 border rounded-lg">
            <p
              className={`text-lg font-medium ${
                isOutOfStock ? "text-red-600" : "text-green-600"
              }`}
            >
              {isOutOfStock ? (
                t("productDetail.stock.outOfStock", "Out of Stock")
              ) : (
                <>
                  <FaCheckCircle className="inline-block mr-2" />
                  {t(
                    "productDetail.stock.readyToShip",
                    "In Stock - Ready to ship",
                  )}
                </>
              )}
            </p>
            {stockQuantity > 0 && stockQuantity <= 10 && (
              <p className="text-sm text-orange-500 mt-1">
                {t(
                  "productDetail.stock.lowStock",
                  "Only {{count}} left in stock - order soon.",
                  { count: stockQuantity },
                )}
              </p>
            )}
          </div>

          {/* Trust Highlights */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <div className="rounded-lg border border-[#efe3e9] bg-[#fff9fc] p-3 text-center">
              <FaShieldAlt className="mx-auto text-[#9a5878] mb-1" />
              <p className="text-xs font-semibold text-gray-700">
                {t("productDetail.trust.secureOrder", "Secure Order")}
              </p>
            </div>
            <div className="rounded-lg border border-[#efe3e9] bg-[#fff9fc] p-3 text-center">
              <FaUndoAlt className="mx-auto text-[#9a5878] mb-1" />
              <p className="text-xs font-semibold text-gray-700">
                {t("productDetail.trust.easyReturns", "Easy Returns")}
              </p>
            </div>
            <div className="rounded-lg border border-[#efe3e9] bg-[#fff9fc] p-3 text-center">
              <FaHeadset className="mx-auto text-[#9a5878] mb-1" />
              <p className="text-xs font-semibold text-gray-700">
                {t("productDetail.trust.support247", "Support 24/7")}
              </p>
            </div>
          </div>

          {/* Shipping Options */}
          <div className="mb-6 rounded-xl border border-gray-200 p-4 bg-white">
            <h3 className="text-base font-semibold text-gray-900 mb-3">
              {t("productDetail.shipping.methods", "Shipping Methods")}
            </h3>
            <div className="space-y-2">
              {shippingOptions.map((option) => {
                const isSelected = selectedShipping === option.key;
                return (
                  <button
                    key={option.key}
                    onClick={() => setSelectedShipping(option.key)}
                    className={`w-full text-left rounded-lg border px-3 py-2.5 transition-all ${
                      isSelected
                        ? "border-[#AA7F96] bg-[#fcf7fa]"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-gray-800">
                        {option.icon}
                        <span className="font-medium text-sm">
                          {option.title}
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-[#83375B]">
                        {option.price}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{option.eta}</p>
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-gray-600">
              {t("productDetail.shipping.selected", "Selected")}:{" "}
              <span className="font-semibold">
                {selectedShippingInfo.title}
              </span>
              {" · "}
              {selectedShippingInfo.eta}
            </p>
          </div>

          {/* Description (Uses dangerouslySetInnerHTML) */}
          <div className="mt-6">
            <h3 className="text-xl font-semibold mb-2 text-gray-800 border-b pb-1">
              {t("productDetail.detailsSection", "Product Details")}
            </h3>
            <div className="text-gray-600 leading-relaxed text-sm">
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    getLocalizedDescription(product) ||
                      t(
                        "productDetail.noDescription",
                        "No detailed description available.",
                      ),
                  ),
                }}
              />{" "}
            </div>{" "}
            {Array.isArray(tags) && tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-[#F4E6EC] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#83375B]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* External Links - removed from info section, now in purchase box */}
        </div>

        {/* 3. Purchase Box (Right Sidebar) */}
        <div className="bg-white lg:w-1/5 p-4 mt-0 lg:mt-0 sticky top-4 self-start border border-gray-300 rounded-xl shadow-2xl">
          <div className="p-4 space-y-4">
            {/* Price (Repeated for visibility in the sidebar, common in e-commerce) */}
            <p className="text-center text-3xl font-extrabold text-[#AA7F96]">
              {price} TL
            </p>
            {/* uncomment if you want to show stock status here as well */}
            {/* Status/Shipping */}
            {/* <div className="text-sm text-green-700 font-medium flex items-center border-b pb-3">
              <MdOutlineLocalShipping className="mr-2 text-xl" />
              {t(
                "productDetail.freeShipping",
                "Free shipping on orders over 1000 TL",
              )}
            </div> */}

            {/* <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs text-gray-500">
                {t(
                  "productDetail.shipping.selectedDelivery",
                  "Selected delivery",
                )}
              </p>
              <p className="text-sm font-semibold text-gray-800">
                {selectedShippingInfo.title}
              </p>
              <p className="text-xs text-gray-600 mt-0.5">
                {selectedShippingInfo.eta} · {selectedShippingInfo.price}
              </p>
              <div className="mt-2 h-2 rounded-full bg-gray-200 overflow-hidden">
                <div
                  className="h-full bg-[#AA7F96]"
                  style={{ width: `${freeShippingProgress}%` }}
                />
              </div>
              <p className="mt-1 text-[11px] text-gray-500">
                {remainingForFreeShipping > 0
                  ? t(
                      "productDetail.shipping.leftForFree",
                      "{{amount}} TL left for free shipping",
                      { amount: remainingForFreeShipping },
                    )
                  : t(
                      "productDetail.shipping.unlockedFree",
                      "You unlocked free shipping",
                    )}
              </p>
            </div> */}

            {ENABLE_CART_UI && (
              <>
                {/* Quantity Selector */}
                <div className="flex items-center space-x-2">
                  <label className="font-medium text-gray-700">
                    {t("quantity", "Qty:")}
                  </label>
                  <div className="flex items-center border border-gray-300 rounded-full">
                    <button
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                      className="p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-50 rounded-l-full"
                    >
                      <FaMinus className="w-3 h-3" />
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      readOnly
                      className="w-10 text-center focus:outline-none"
                    />
                    <button
                      onClick={() => handleQuantityChange(1)}
                      disabled={isOutOfStock || quantity >= stockQuantity}
                      className="p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-50 rounded-r-full"
                    >
                      <FaPlus className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Add to Cart Button */}
                <button
                  onClick={handleAddToCart}
                  disabled={isOutOfStock}
                  className={`w-full py-3 rounded-full text-white font-bold transition-all duration-200 shadow-lg ${
                    isOutOfStock
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-[#83375B] hover:bg-[#AA7F96]"
                  }`}
                >
                  <FaShoppingCart className="inline-block mr-2" />
                  {localCartItem
                    ? t("product.updateCart", "Update Cart")
                    : t("product.addToCart", "Add to Cart")}
                </button>
              </>
            )}

            {/* Wishlist Button */}
            <button
              onClick={handleFavoriteClick}
              disabled={isWishlistLoading}
              className={`w-full py-2.5 px-3 border rounded-full transition-all duration-200 ${
                isFavorited
                  ? "border-[#efc6c6] bg-[#fff7f7] text-[#a34a4a]"
                  : "border-gray-300 bg-white text-gray-700 hover:border-[#d7a9b9] hover:bg-[#fcf7fa]"
              } ${isWishlistLoading ? "opacity-80 cursor-wait" : ""}`}
            >
              <div className="flex items-center justify-center gap-2 text-sm font-medium">
                <span
                  className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${
                    isFavorited ? "text-[#c25252]" : "text-gray-500"
                  } ${isWishlistLoading ? "animate-pulse" : ""}`}
                >
                  {isFavorited ? (
                    <FaHeart size={13} />
                  ) : (
                    <FaRegHeart size={13} />
                  )}
                </span>
                <span>
                  {isWishlistLoading
                    ? t("product.updatingWishlist", "Updating wishlist...")
                    : isFavorited
                      ? t("product.savedWishlist", "Saved")
                      : t("product.saveToWishlist", "Save to Wishlist")}
                </span>
              </div>
            </button>

            <div className="rounded-xl border border-[#e8d8e0] bg-gradient-to-br from-[#fff9fc] via-white to-[#f9f1f5] p-3.5">
              <p className="text-sm font-semibold text-[#6f2f4c]">
                {t(
                  "productDetail.quoteCard.title",
                  "Need a quote for this exact product?",
                )}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {t(
                  "productDetail.quoteCard.description",
                  "Send a prefilled inquiry with product, model, and quantity in one click.",
                )}
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  onClick={() => openInquiryModal("quote")}
                  className="rounded-lg bg-[#83375B] hover:bg-[#9b4b70] text-white text-xs font-semibold py-2 transition-colors"
                >
                  {t("productDetail.quoteCard.requestQuote", "Request Quote")}
                </button>
                <button
                  onClick={() => openInquiryModal("inquiry")}
                  className="rounded-lg border border-[#d8b7c7] bg-white hover:bg-[#fcf7fa] text-[#83375B] text-xs font-semibold py-2 transition-colors"
                >
                  {t("productDetail.quoteCard.askQuestion", "Ask a Question")}
                </button>
              </div>
            </div>

            {/* Buy on Shops */}
            <div className="space-y-3 pt-2 border-t border-gray-100">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 text-center">
                {t("productDetail.availableOn", "Available on")}
              </p>
              {marketplaceButtons.map((shop) => {
                const hasLink = Boolean(shop.href);

                if (hasLink) {
                  return (
                    <a
                      key={shop.key}
                      href={shop.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between w-full px-4 py-2.5 rounded-full font-semibold text-sm text-white transition-all duration-200 shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-100"
                      style={{ background: shop.gradient }}
                    >
                      <span>{shop.label}</span>
                      <FaExternalLinkAlt className="opacity-80" size={12} />
                    </a>
                  );
                }

                return (
                  <div
                    key={shop.key}
                    className="flex items-center justify-between w-full px-4 py-2.5 rounded-full font-semibold text-sm bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                  >
                    <span>{shop.label}</span>
                    <span className="text-[11px] uppercase tracking-wider">
                      {t("comingSoon.badge", "Coming soon")}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <section className="mt-10 rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
          <div
            className="flex gap-3 overflow-x-scroll px-2 scrollbar-thin scrollbar-thumb-[#e0bbe4] scrollbar-track-[#f7eaf7]"
            style={{
              WebkitOverflowScrolling: "touch",
              scrollSnapType: "x mandatory",
            }}
          >
            <p className="text-sm text-gray-500">
              {t(
                "productDetail.reviews.subtitle",
                "Real feedback from shoppers who purchased this product.",
              )}
            </p>
          </div>
          <div className="self-start sm:self-auto flex flex-col items-start sm:items-end gap-2">
            <button
              onClick={openReviewModal}
              disabled={reviewAccessLoading}
              className="rounded-full bg-[#83375B] hover:bg-[#9b4b70] text-white px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {currentUserReview
                ? t("productDetail.reviews.editReview", "Edit Your Review")
                : t("productDetail.reviews.writeReview", "Write a Review")}
            </button>
            {user &&
              !reviewAccessLoading &&
              !currentUserReview &&
              !canReviewProduct && (
                <p className="text-xs text-gray-500">
                  {t(
                    "productDetail.reviews.purchaseRequired",
                    "Only customers who purchased this product can write a review.",
                  )}
                </p>
              )}
            {currentUserReview && !currentUserReview.approved && (
              <p className="text-xs text-amber-700">
                {t(
                  "productDetail.reviews.pendingModeration",
                  "Your review is pending moderation.",
                )}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="rounded-xl border border-[#eadde4] bg-[#fff9fc] p-4">
            <p className="text-sm text-gray-500">
              {t("productDetail.reviews.averageRating", "Average Rating")}
            </p>
            <div className="flex items-end gap-2 mt-1">
              <span className="text-4xl font-extrabold text-[#83375B]">
                {averageRating ? averageRating.toFixed(1) : "0.0"}
              </span>
              <span className="text-sm text-gray-500 mb-1">/ 5</span>
            </div>
            <div className="flex items-center gap-1 mt-2 text-[#d28c2c]">
              {[1, 2, 3, 4, 5].map((star) => {
                const filled = star <= Math.round(averageRating);
                return filled ? (
                  <FaStar key={star} size={14} />
                ) : (
                  <FaRegStar key={star} size={14} />
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {t("productDetail.reviews.basedOn", "Based on {{count}} review", {
                count: reviews.length,
              })}
              {reviews.length === 1
                ? ""
                : t("productDetail.reviews.pluralS", "s")}
            </p>
          </div>

          <div className="lg:col-span-2 rounded-xl border border-gray-200 p-4">
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = ratingsBreakdown[star] || 0;
                const percent = reviews.length
                  ? Math.round((count / reviews.length) * 100)
                  : 0;
                return (
                  <div key={star} className="flex items-center gap-2 text-sm">
                    <span className="w-9 text-gray-600">{star}★</span>
                    <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full bg-[#AA7F96]"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <span className="w-8 text-right text-gray-500">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {t(
                  "productDetail.reviews.sortHelp",
                  "Sorted by quality and recency for faster decision making.",
                )}
              </p>
              <select
                className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                value={reviewSort}
                onChange={(e) => setReviewSort(e.target.value)}
              >
                <option value="newest">
                  {t("productDetail.reviews.sortNewest", "Newest")}
                </option>
                <option value="highest">
                  {t("productDetail.reviews.sortHighest", "Highest Rated")}
                </option>
                <option value="lowest">
                  {t("productDetail.reviews.sortLowest", "Lowest Rated")}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {reviewsLoading && (
            <p className="text-sm text-gray-500">
              {t("productDetail.reviews.loading", "Loading reviews...")}
            </p>
          )}

          {!reviewsLoading && sortedReviews.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
              {t(
                "productDetail.reviews.empty",
                "No reviews yet. Be the first to share your experience.",
              )}
            </div>
          )}

          {!reviewsLoading &&
            sortedReviews.map((review) => (
              <article
                key={review.review_id}
                className="rounded-lg border border-gray-200 p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-gray-800">
                    {formatReviewerName(review)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {review.createdAt
                      ? new Date(review.createdAt).toLocaleDateString()
                      : "Recent"}
                  </p>
                </div>
                <div className="flex items-center gap-1 mt-1 text-[#d28c2c]">
                  {[1, 2, 3, 4, 5].map((star) =>
                    star <= (review.rating || 0) ? (
                      <FaStar key={star} size={13} />
                    ) : (
                      <FaRegStar key={star} size={13} />
                    ),
                  )}
                </div>
                <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                  {review.comment ||
                    t(
                      "productDetail.reviews.noComment",
                      "No comment provided.",
                    )}
                </p>
                {review.image_url && (
                  <div className="mt-3">
                    <img
                      src={getImageUrl(review.image_url)}
                      alt="Review upload"
                      className="h-28 w-28 rounded-lg object-cover border border-gray-200"
                      onError={(e) => {
                        console.error("Image failed to load:", e.target.src, e);
                        e.target.src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='112' height='112'%3E%3Crect fill='%23e5e7eb' width='112' height='112'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23999' font-size='12' font-family='Arial'%3EImage Error%3C/text%3E%3C/svg%3E";
                      }}
                      onLoad={() =>
                        console.log(
                          "Image loaded successfully:",
                          getImageUrl(review.image_url),
                        )
                      }
                    />
                  </div>
                )}
              </article>
            ))}
        </div>
      </section>

      {/* Q&A Section */}
      <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
        <h3 className="text-xl font-bold text-gray-900">
          {t("productDetail.qa.title", "Questions & Answers")}
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          {t(
            "productDetail.qa.subtitle",
            "Common questions about delivery, returns, and product reliability.",
          )}
        </p>

        <div className="mt-4 space-y-2">
          {qaItems.map((item) => {
            const isOpen = openQaId === item.id;
            return (
              <div
                key={item.id}
                className="border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
                  onClick={() => setOpenQaId(isOpen ? "" : item.id)}
                >
                  <span className="font-medium text-gray-800 text-sm sm:text-base">
                    {item.question}
                  </span>
                  <FaChevronDown
                    className={`text-gray-500 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed">
                    {item.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {isInquiryModalOpen && (
        <div
          className="fixed inset-0 z-[95] bg-black/50 flex items-center justify-center p-4"
          onClick={closeInquiryModal}
        >
          <div
            className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 bg-gradient-to-r from-[#83375B] to-[#AA7F96] text-white flex items-center justify-between">
              <div>
                <h4 className="text-lg font-bold">
                  {inquiryType === "quote"
                    ? t(
                        "productDetail.inquiry.requestProductQuote",
                        "Request Product Quote",
                      )
                    : t(
                        "productDetail.inquiry.productInquiry",
                        "Product Inquiry",
                      )}
                </h4>
                <p className="text-xs text-white/90 mt-0.5">
                  {t(
                    "productDetail.inquiry.prefilledHelp",
                    "Your message is prefilled for the currently opened product.",
                  )}
                </p>
              </div>
              <button
                onClick={closeInquiryModal}
                className="text-white/90 hover:text-white"
              >
                <MdClose size={22} />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
              <div className="rounded-xl border border-gray-200 p-3 flex items-center gap-3">
                {images?.[0]?.image_url && (
                  <img
                    src={getImageUrl(images[0].image_url)}
                    alt={getLocalizedName(product)}
                    className="h-14 w-14 rounded-lg object-cover border border-gray-200"
                  />
                )}
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {getLocalizedName(product)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("modelCode", "Model")}:{" "}
                    {model_code || sku || t("profile.na", "N/A")} ·{" "}
                    {t("productDetail.inquiry.listed", "Listed")}: {price} TL
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  value={inquiryForm.fullName}
                  onChange={(e) =>
                    handleInquiryFieldChange("fullName", e.target.value)
                  }
                  placeholder={t(
                    "productDetail.inquiry.fullNamePlaceholder",
                    "Your full name",
                  )}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#AA7F96]"
                />
                <input
                  type="email"
                  value={inquiryForm.email}
                  onChange={(e) =>
                    handleInquiryFieldChange("email", e.target.value)
                  }
                  placeholder={t(
                    "productDetail.inquiry.emailPlaceholder",
                    "Your email",
                  )}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#AA7F96]"
                />
                <input
                  type="text"
                  value={inquiryForm.phone}
                  onChange={(e) =>
                    handleInquiryFieldChange("phone", e.target.value)
                  }
                  placeholder={t(
                    "productDetail.inquiry.phonePlaceholder",
                    "Phone / WhatsApp",
                  )}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#AA7F96]"
                />
                <input
                  type="text"
                  value={inquiryForm.company}
                  onChange={(e) =>
                    handleInquiryFieldChange("company", e.target.value)
                  }
                  placeholder={t(
                    "productDetail.inquiry.companyPlaceholder",
                    "Company (optional)",
                  )}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#AA7F96]"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  {t("productDetail.inquiry.quantity", "Quantity")}
                </label>
                <input
                  type="number"
                  min={1}
                  value={inquiryForm.quantity}
                  onChange={(e) =>
                    handleInquiryFieldChange("quantity", e.target.value)
                  }
                  className="mt-1 w-full sm:w-40 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#AA7F96]"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  {t("productDetail.inquiry.message", "Message")}
                </label>
                <textarea
                  rows={8}
                  value={inquiryForm.message}
                  onChange={(e) =>
                    handleInquiryFieldChange("message", e.target.value)
                  }
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#AA7F96]"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <a
                  href={inquiryEmailHref}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#83375B] hover:bg-[#9b4b70] text-white text-sm font-semibold py-2.5 transition-colors"
                >
                  <FaEnvelope size={14} />
                  {t("productDetail.inquiry.sendByEmail", "Send by Email")}
                </a>
                <a
                  href={inquiryWhatsappHref || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center justify-center gap-2 rounded-lg text-sm font-semibold py-2.5 transition-colors ${
                    inquiryWhatsappHref
                      ? "bg-[#25D366] hover:bg-[#1EBE57] text-white"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                  onClick={(e) => {
                    if (!inquiryWhatsappHref) e.preventDefault();
                  }}
                >
                  <FaWhatsapp size={15} />
                  WhatsApp
                </a>
                <button
                  onClick={copyInquiryMessage}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-semibold py-2.5 transition-colors"
                >
                  <MdContentCopy size={15} />
                  {t("productDetail.inquiry.copyMessage", "Copy Message")}
                </button>
              </div>

              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                  Sales email: {salesEmail}
                </p>
                <button
                  onClick={() => navigate("/contact-us")}
                  className="text-xs font-semibold text-[#83375B] hover:underline"
                >
                  {t("productDetail.inquiry.openContact", "Open Contact Page")}
                </button>
              </div>

              {inquiryFeedback && (
                <p className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  {inquiryFeedback}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Write Review Modal */}
      {isReviewModalOpen && (
        <div
          className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4"
          onClick={closeReviewModal}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-gray-900">
                {currentUserReview
                  ? t("productDetail.reviews.editReview", "Edit Your Review")
                  : t("productDetail.reviews.writeReview", "Write a Review")}
              </h4>
              <button
                onClick={closeReviewModal}
                className="text-gray-500 hover:text-gray-800"
              >
                <MdClose size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmitReview} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("productDetail.reviews.rating", "Rating")}
                </label>
                <div className="flex items-center gap-1 text-[#d28c2c]">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() =>
                        setNewReview((prev) => ({ ...prev, rating: star }))
                      }
                    >
                      {star <= newReview.rating ? (
                        <FaStar size={20} />
                      ) : (
                        <FaRegStar size={20} />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("productDetail.reviews.comment", "Comment")}
                </label>
                <textarea
                  rows={4}
                  value={newReview.comment}
                  onChange={(e) =>
                    setNewReview((prev) => ({
                      ...prev,
                      comment: e.target.value,
                    }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#AA7F96]"
                  placeholder={t(
                    "productDetail.reviews.commentPlaceholder",
                    "How was the product quality, fit, delivery, and overall experience?",
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("productDetail.reviews.addPhoto", "Add Photo (optional)")}
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleReviewImageChange}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-[#f5eaf0] file:px-3 file:py-1.5 file:text-[#83375B] file:font-semibold hover:file:bg-[#eddce6]"
                />
                {newReviewImagePreview && (
                  <div className="mt-2 flex items-start gap-3">
                    <img
                      src={newReviewImagePreview}
                      alt="Selected review upload"
                      className="h-24 w-24 rounded-lg object-cover border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveReviewImage}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      {t("productDetail.reviews.removeImage", "Remove image")}
                    </button>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmittingReview}
                className="w-full rounded-full bg-[#83375B] hover:bg-[#9b4b70] text-white py-2.5 font-semibold disabled:opacity-70"
              >
                {isSubmittingReview
                  ? currentUserReview
                    ? t("productDetail.reviews.saving", "Saving...")
                    : t("productDetail.reviews.submitting", "Submitting...")
                  : currentUserReview
                    ? t("productDetail.reviews.saveReview", "Save Review")
                    : t("productDetail.reviews.submitReview", "Submit Review")}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Related Products Carousel Section */}
      {relatedProducts.length > 0 && (
        <section className="mt-10 rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
          <h3 className="mb-6 text-2xl font-bold text-gray-900">
            {t("productDetail.relatedProducts", "Related Products")}
          </h3>
          {relatedProductsLoading ? (
            <div className="flex justify-center py-8">
              <p className="text-gray-500">{t("loading", "Loading...")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedProducts.map((relatedProduct) => {
                const relatedName =
                  relatedProduct[`name_${currentLanguage}`] ||
                  relatedProduct.name_en;
                const relatedImage =
                  relatedProduct.images && relatedProduct.images[0]
                    ? getImageUrl(relatedProduct.images[0].image_url)
                    : "https://placehold.co/400x300/E0BBE4/FFFFFF?text=No+Image";

                return (
                  <div
                    key={relatedProduct.product_id}
                    onClick={() =>
                      navigate(
                        `/product/${getProductRouteParam(relatedProduct) || relatedProduct.product_id}`,
                        {
                          state: { product: relatedProduct },
                        },
                      )
                    }
                    className="cursor-pointer rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300 bg-white"
                  >
                    <div className="h-48 overflow-hidden bg-gray-100">
                      <img
                        src={relatedImage}
                        alt={relatedName}
                        className="h-full w-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-semibold text-gray-900 line-clamp-2 text-sm">
                        {relatedName}
                      </h4>
                      <div className="mt-2 flex items-center gap-2">
                        {relatedProduct.sale_price ? (
                          <>
                            <span className="text-xs line-through text-gray-500">
                              {relatedProduct.price} TL
                            </span>
                            <span className="text-lg font-bold text-red-600">
                              {relatedProduct.sale_price} TL
                            </span>
                          </>
                        ) : (
                          <span className="text-lg font-bold text-[#83375B]">
                            {relatedProduct.price} TL
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
